'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/Button';
import {
  HomeIcon,
  ChatBubbleLeftRightIcon,
  ArrowLeftIcon,
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  StarIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

// Floating stars component
const FloatingStars = () => {
  const stars = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 4 + 2,
    animationDelay: Math.random() * 3,
    duration: Math.random() * 3 + 2
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute animate-pulse"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            animationDelay: `${star.animationDelay}s`,
            animationDuration: `${star.duration}s`
          }}
        >
          <StarIcon 
            className="text-primary/30" 
            style={{ 
              width: `${star.size}px`, 
              height: `${star.size}px` 
            }} 
          />
        </div>
      ))}
    </div>
  );
};

// Interactive UFO component
const InteractiveUFO = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isBeaming, setIsBeaming] = useState(false);
  const ufoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (ufoRef.current) {
        const rect = ufoRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        const deltaX = (e.clientX - centerX) * 0.1;
        const deltaY = (e.clientY - centerY) * 0.1;
        
        setPosition({ x: deltaX, y: deltaY });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div 
      ref={ufoRef}
      className="relative cursor-pointer select-none"
      onClick={() => setIsBeaming(!isBeaming)}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        transition: 'transform 0.1s ease-out'
      }}
    >
      {/* UFO Body */}
      <div className="relative w-32 h-16 mx-auto">
        {/* Tractor Beam */}
        {isBeaming && (
          <div className="absolute top-16 left-1/2 transform -translate-x-1/2">
            <div className="w-0 h-0 border-l-[50px] border-r-[50px] border-t-[60px] border-l-transparent border-r-transparent border-t-primary/20 animate-pulse">
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                <SparklesIcon className="w-6 h-6 text-primary animate-spin" />
              </div>
            </div>
          </div>
        )}
        
        {/* UFO Main Body */}
        <div className="relative">
          <div className="w-32 h-16 bg-gradient-to-b from-primary/80 to-primary rounded-full shadow-2xl">
            {/* UFO Top Dome */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-20 h-12 bg-gradient-to-b from-secondary/60 to-secondary/20 rounded-full border border-secondary/30"></div>
            
            {/* Lights */}
            <div className="absolute bottom-2 left-4 w-2 h-2 bg-accent rounded-full animate-pulse"></div>
            <div className="absolute bottom-2 left-8 w-2 h-2 bg-warning rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-2 left-12 w-2 h-2 bg-success rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-2 right-4 w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            <div className="absolute bottom-2 right-8 w-2 h-2 bg-warning rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>
      </div>
      
      {/* Hover instruction */}
      <p className="text-xs text-muted-foreground dark:text-blue-300 mt-4 text-center animate-bounce">
        Click me! 🛸
      </p>
    </div>
  );
};

// Glitch text effect for 404
const GlitchText = () => {
  const [glitching, setGlitching] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitching(true);
      setTimeout(() => setGlitching(false), 200);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative">
      <div className={`text-8xl md:text-9xl font-bold font-mono transition-all duration-200 ${
        glitching 
          ? 'text-destructive transform skew-x-2 animate-pulse' 
          : 'gradient-text'
      }`}>
        4🚀4
      </div>
      
      {glitching && (
        <>
          <div className="absolute inset-0 text-8xl md:text-9xl font-bold font-mono text-primary/50 transform translate-x-1 -translate-y-1 animate-pulse">
            4🚀4
          </div>
          <div className="absolute inset-0 text-8xl md:text-9xl font-bold font-mono text-secondary/50 transform -translate-x-1 translate-y-1 animate-pulse">
            4🚀4
          </div>
        </>
      )}
    </div>
  );
};

// Floating astronaut
const FloatingAstronaut = () => {
  return (
    <div className="absolute top-10 right-10 animate-float">
      <div className="w-20 h-20 bg-gradient-to-br from-muted/20 to-muted/10 dark:from-muted-foreground/10 dark:to-muted-foreground/5 rounded-full flex items-center justify-center backdrop-blur-sm border border-muted dark:border-muted-foreground/20">
        <div className="text-2xl">🧑‍🚀</div>
      </div>
    </div>
  );
};

export default function NotFound() {
  const [messageIndex, setMessageIndex] = useState(0);
  const [showEasterEgg, setShowEasterEgg] = useState(false);

  const messages = [
    "Houston, we have a problem... this page is floating in space! 🚀",
    "Our PR rockets missed this page's orbit! 🌌",
    "This page was abducted by aliens... probably for its great content! 👽",
    "Error 404: Page launched into the stratosphere! 🛸"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [messages.length]);

  // Konami code easter egg
  useEffect(() => {
    const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];
    let konamiIndex = 0;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          setShowEasterEgg(true);
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-orange-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Cosmic background overlay for dark mode */}
      <div className="absolute inset-0 dark:bg-gradient-to-br dark:from-indigo-900/50 dark:via-purple-900/50 dark:to-pink-900/50"></div>
      
      {/* Animated nebula effect - adapts to theme */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 dark:bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 dark:bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/5 dark:bg-accent/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Floating stars */}
      <FloatingStars />

      {/* Floating astronaut */}
      <FloatingAstronaut />

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-4xl space-y-12 animate-in">
          {/* Interactive UFO */}
          <div className="mb-8">
            <InteractiveUFO />
          </div>

          {/* 404 with glitch effect */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 blur-3xl animate-pulse-scale"></div>
            <div className="relative">
              <div className="liquid-glass p-8 rounded-3xl mx-auto max-w-md backdrop-blur-xl">
                <GlitchText />
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-accent to-accent/80 rounded-2xl flex items-center justify-center animate-bounce-in animation-delay-300 shadow-2xl">
                  <RocketLaunchIcon className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Animated message */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-foreground via-primary to-secondary bg-clip-text text-transparent">
                Lost in Space
              </h1>
              <div className="h-16 flex items-center justify-center">
                <p className="text-xl text-foreground/80 dark:text-blue-100 max-w-2xl mx-auto leading-relaxed transition-all duration-500 ease-in-out">
                  {messages[messageIndex]}
                </p>
              </div>
            </div>

            {/* Mission Control Panel */}
            <div className="liquid-glass p-8 rounded-3xl backdrop-blur-xl max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-foreground dark:text-white mb-6 flex items-center justify-center gap-3">
                <GlobeAltIcon className="h-8 w-8 text-primary" />
                Mission Control
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="liquid-glass p-6 rounded-2xl backdrop-blur-sm group cursor-pointer hover:scale-105 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/40 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform">
                    <MagnifyingGlassIcon className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-semibold text-foreground dark:text-white mb-2">Navigation Check</h4>
                  <p className="text-sm text-muted-foreground dark:text-blue-200">
                    Verify coordinates and try the URL again
                  </p>
                </div>
                
                <div className="liquid-glass p-6 rounded-2xl backdrop-blur-sm group cursor-pointer hover:scale-105 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-secondary/20 to-secondary/40 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-secondary" />
                  </div>
                  <h4 className="font-semibold text-foreground dark:text-white mb-2">Ground Control</h4>
                  <p className="text-sm text-muted-foreground dark:text-blue-200">
                    Contact our AI mission specialist for assistance
                  </p>
                </div>
              </div>
            </div>

            {/* Launch buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center pt-8">
              <Button asChild variant="glow" size="xl" className="group relative overflow-hidden">
                <Link href="/" className="flex items-center gap-3">
                  <HomeIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                  Return to Base
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </Link>
              </Button>
              
              <Button 
                variant="glass" 
                size="xl" 
                onClick={() => window.history.back()}
                className="group text-foreground dark:text-white border-border dark:border-white/20 hover:border-border/60 dark:hover:border-white/40"
              >
                <ArrowLeftIcon className="h-6 w-6 group-hover:-translate-x-1 transition-transform" />
                Previous Mission
              </Button>
            </div>

            {/* Easter egg */}
            {showEasterEgg && (
              <div className="liquid-glass p-6 rounded-2xl mt-8 max-w-md mx-auto animate-bounce-in backdrop-blur-xl">
                <div className="text-center">
                  <div className="text-4xl mb-4">🎉</div>
                  <h4 className="font-semibold text-foreground dark:text-white mb-2">Konami Code Activated!</h4>
                  <p className="text-sm text-muted-foreground dark:text-blue-200">
                    You've unlocked the secret space mission! Even lost in space, you found the Easter egg. That's some stellar PR navigation! 🚀✨
                  </p>
                </div>
              </div>
            )}

            {/* Pro tip with cosmic theme */}
            <div className="liquid-glass p-6 rounded-2xl mt-8 max-w-2xl mx-auto backdrop-blur-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-accent/20 to-accent/40 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="h-6 w-6 text-accent animate-spin" style={{ animationDuration: '3s' }} />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-foreground dark:text-white mb-3">🚀 Space PR Wisdom</h4>
                  <p className="text-sm text-muted-foreground dark:text-blue-200 leading-relaxed">
                    Just like space exploration, great PR is about turning the unexpected into an opportunity. 
                    Every 404 is a chance to show personality and guide users back to your stellar content. 
                    <span className="block mt-2 text-xs opacity-75">
                      Try the Konami code: ↑↑↓↓←→←→BA
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}